import { ApiClient } from '../api-client';
import {
  ITenantConfigOptions,
  ITenantDetailsResponseDataRaw,
  ITenantEnvironmentConfigResponse,
} from '../tenants-api';
import { fetchTenantEnvironmentUrl, tenantConfigUrl } from '../urls';

import { formatTenantList, formatTenantEnvDetails } from './transformers';

/**
 * This api client is used to fetch data associated with multi tenant configurations.
 * It is a singleton class that uses the Axios instance to make requests.
 * The class is initialized with the base URL of the tenant API.
 * The class is used to fetch the tenant list and tenant config based on the identifier.
 */
export class TenantAPIClient extends ApiClient {
  private static classInstance?: TenantAPIClient;

  private constructor() {
    super({
      apiBaseURL: 'TENANT_BASE_URL',
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new TenantAPIClient();
    }

    return this.classInstance;
  };

  /**
   * Fetches the list of tenants and their environments.
   * Reference[PROD]: https://tenants.northladder.info/public.json
   * Reference[UAT]: https://tenants-uat.asancash.com/public.json
   */
  public fetchTenantList = async () => {
    const response = await this.get<ITenantEnvironmentConfigResponse[]>(
      fetchTenantEnvironmentUrl(),
      {
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    // NOTE: This blindly assumes the variable at this point will never be messed up.
    return formatTenantList(
      response.data?.[0] as ITenantEnvironmentConfigResponse
    );
  };

  /**
   * Fetches the tenant config based on the identifier, this contains the
   * environment details and other tenant specific configurations like base url,
   * cdn url, contact details, etc.
   * Reference[PROD]: https://tenants.northladder.info/northladder.com.json
   * Reference[UAT]: https://tenants-uat.asancash.com/asancash.com.json
   */
  public fetchTenantConfig = async ({
    identifier,
    logoUrl = '',
  }: ITenantConfigOptions) => {
    const response = await this.get<ITenantDetailsResponseDataRaw>(
      tenantConfigUrl(identifier),
      {
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    return formatTenantEnvDetails(response.data, identifier, logoUrl);
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for tenant config related services.
 */
export const TENANT_CONFIG_API = TenantAPIClient.getClientInstance();
